/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher } from '../events';
import { Logger } from '../logger';

const logger = Logger.getInstance();

interface AdMessage {
  nodals: {
    event: string;
    payload: any;
  };
}

enum NodalsMessageEvent {
  AD_INTERACTION = 'ad.interaction',
  AD_SERVE = 'ad.serve',
  OPPORTUNITY_DATA = 'opportunity.data',
}

interface AdCommunicatorEvents {
  AdInteraction: {
    window: Window;
  };
  TaggedAdServed: {
    adUnitPath: string;
    advertiserId: string;
    deliveryId: string;
    impressionId: string;
    lineItemId: string;
    creativeId?: string;
    senderInfo?: string;
    window: Window;
  };
}

class AdCommunicator extends EventDispatcher<AdCommunicatorEvents> {
  private static instance: AdCommunicator;

  private constructor() {
    super();
    window.addEventListener('message', this.messageHandler.bind(this));
  }

  public static getInstance(): AdCommunicator {
    if (!AdCommunicator.instance) {
      AdCommunicator.instance = new AdCommunicator();
    }
    return AdCommunicator.instance;
  }

  private messageHandler(event: MessageEvent): void {
    const payload: AdMessage = event.data;
    if (typeof payload !== 'object' || !payload?.nodals?.event || !event.source) {
      return;
    }
    logger.info(`Message received ${JSON.stringify(payload)}`);
    switch (payload.nodals.event) {
      case NodalsMessageEvent.AD_SERVE:
        this.dispatch('TaggedAdServed', {
          adUnitPath: (payload.nodals?.payload?.gad || payload.nodals?.payload?.aut) ?? '',
          advertiserId: payload.nodals?.payload?.advid ?? '',
          impressionId: payload.nodals?.payload?.id ?? '',
          lineItemId: payload.nodals?.payload?.lid ?? '',
          deliveryId: payload.nodals?.payload?.did ?? '',
          creativeId: payload.nodals?.payload?.crid,
          senderInfo: payload.nodals?.payload?.dl,
          window: event.source as Window,
        });
        break;
      case NodalsMessageEvent.AD_INTERACTION:
        this.dispatch('AdInteraction', {
          window: event.source as Window,
        });
        break;
      default:
        logger.error(`Unhandled message event: ${payload.nodals.event}`);
    }
  }

  private prepare_object_for_transport(obj: any, visited = new WeakSet()): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (visited.has(obj)) {
      return {};
    }
    visited.add(obj);

    if (Array.isArray(obj)) {
      return obj.map((item) => this.prepare_object_for_transport(item, visited));
    }

    if (obj instanceof Date || obj instanceof RegExp) {
      return obj;
    }

    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value !== 'function') {
        result[key] = this.prepare_object_for_transport(value, visited);
      }
    }

    return result;
  }

  public sendMessage(
    source: Window,
    eventType: NodalsMessageEvent,
    payload: any,
    config?: any
  ): void {
    const message: AdMessage = {
      nodals: {
        event: eventType,
        payload,
      },
    };
    switch (eventType) {
      case NodalsMessageEvent.OPPORTUNITY_DATA:
        if (config) {
          message.nodals.payload.config = this.prepare_object_for_transport(config);
        }
        break;
    }
    logger.info(`Sending message ${JSON.stringify(message)} to creative tag`);
    source.postMessage(message, '*');
  }
}

export { AdCommunicator, NodalsMessageEvent, type AdCommunicatorEvents };
